import React, { useState, useEffect, useCallback } from "react";
import { API } from "../hooks/useApi";

const s = {
  card: { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "8px", padding: "16px" },
  title: { fontSize: "14px", fontWeight: 600, color: "#60a5fa", marginBottom: "12px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "12px" },
  th: { textAlign: "left", padding: "8px 10px", color: "#94a3b8", borderBottom: "1px solid #1e2a45" },
  td: { padding: "8px 10px", borderBottom: "1px solid #0f172a", color: "#e2e8f0" },
  badge: (role) => ({
    padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 600,
    background: role === "admin" ? "#1e40af" : role === "trader" ? "#065f46" : "#1e2a45",
    color: role === "admin" ? "#93c5fd" : role === "trader" ? "#6ee7b7" : "#94a3b8",
  }),
  dot: (ok) => ({
    width: "7px", height: "7px", borderRadius: "50%", display: "inline-block",
    background: ok ? "#22c55e" : "#ef4444", marginRight: "6px",
  }),
  btn: (variant) => ({
    padding: "4px 10px", borderRadius: "4px", border: "none", cursor: "pointer",
    fontSize: "11px", fontWeight: 600, marginRight: "4px",
    background: variant === "danger" ? "#7f1d1d" : variant === "primary" ? "#1e40af" : "#1e2a45",
    color: variant === "danger" ? "#fca5a5" : variant === "primary" ? "#93c5fd" : "#94a3b8",
  }),
  form: { display: "flex", gap: "8px", alignItems: "flex-end", marginBottom: "16px", flexWrap: "wrap" },
  input: {
    padding: "7px 10px", borderRadius: "5px", border: "1px solid #1e2a45",
    background: "#0a0f1e", color: "#e2e8f0", fontSize: "12px", outline: "none",
  },
  select: {
    padding: "7px 10px", borderRadius: "5px", border: "1px solid #1e2a45",
    background: "#0a0f1e", color: "#e2e8f0", fontSize: "12px", outline: "none",
  },
  label: { fontSize: "10px", color: "#94a3b8", marginBottom: "2px", display: "block" },
  error: { color: "#f87171", fontSize: "11px", marginBottom: "8px" },
  success: { color: "#4ade80", fontSize: "11px", marginBottom: "8px" },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "viewer" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      const res = await API.get("/auth/users");
      setUsers(res.data.users || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load users");
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    try {
      await API.post("/auth/users", newUser);
      setSuccess(`User '${newUser.username}' created as ${newUser.role}`);
      setNewUser({ username: "", password: "", role: "viewer" });
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create user");
    }
  };

  const handleToggle = async (id, currentActive) => {
    try {
      await API.put(`/auth/users/${id}`, { is_active: !currentActive });
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update user");
    }
  };

  const handleUnlock = async (id) => {
    try {
      await API.put(`/auth/users/${id}`, { unlock: true });
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to unlock user");
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      await API.put(`/auth/users/${id}`, { role });
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to change role");
    }
  };

  return (
    <div style={{ padding: "10px", maxWidth: "900px", margin: "0 auto" }}>
      <div style={s.card}>
        <div style={s.title}>👥 User Management</div>

        {error && <div style={s.error}>⚠️ {error}</div>}
        {success && <div style={s.success}>✅ {success}</div>}

        {/* Create user form */}
        <form style={s.form} onSubmit={handleCreate}>
          <div>
            <label style={s.label}>Username</label>
            <input style={s.input} value={newUser.username} placeholder="username"
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
          </div>
          <div>
            <label style={s.label}>Password</label>
            <input style={s.input} type="password" value={newUser.password} placeholder="min 12 chars"
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
          </div>
          <div>
            <label style={s.label}>Role</label>
            <select style={s.select} value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
              <option value="viewer">Viewer</option>
              <option value="trader">Trader</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button style={s.btn("primary")} type="submit">+ Create User</button>
        </form>

        {/* Users table */}
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Status</th>
              <th style={s.th}>Username</th>
              <th style={s.th}>Role</th>
              <th style={s.th}>Last Login</th>
              <th style={s.th}>Failed</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={s.td}>
                  <span style={s.dot(u.is_active && !u.locked)} />
                  {u.locked ? "🔒 Locked" : u.is_active ? "Active" : "Disabled"}
                </td>
                <td style={s.td}>{u.username}</td>
                <td style={s.td}>
                  <select style={{ ...s.select, padding: "2px 6px", fontSize: "11px" }}
                    value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}>
                    <option value="viewer">viewer</option>
                    <option value="trader">trader</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td style={{ ...s.td, fontSize: "10px", color: "#64748b" }}>
                  {u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}
                </td>
                <td style={{ ...s.td, color: u.failed_attempts > 0 ? "#f87171" : "#64748b" }}>
                  {u.failed_attempts}
                </td>
                <td style={s.td}>
                  {u.locked && (
                    <button style={s.btn("primary")} onClick={() => handleUnlock(u.id)}>🔓 Unlock</button>
                  )}
                  <button style={s.btn(u.is_active ? "danger" : "primary")}
                    onClick={() => handleToggle(u.id, u.is_active)}>
                    {u.is_active ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: "12px", fontSize: "10px", color: "#475569" }}>
          <b>Roles:</b> Admin (full access + user management) · Trader (view + trade execution) · Viewer (read-only dashboard)
        </div>
      </div>
    </div>
  );
}
